import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { ListHeader, ListRow } from "@d12labs/ui/data-list";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@d12labs/ui/dropdown-menu";
import { EmptyState } from "@d12labs/ui/empty-state";
import { Input } from "@d12labs/ui/input";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Pill } from "@d12labs/ui/pill";
import { Toolbar } from "@d12labs/ui/toolbar";
import { cn } from "@d12labs/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
	ArrowUpDown,
	ChevronDown,
	ChevronRight,
	ChevronUp,
	Download,
	Plus,
	Search,
	Users,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { meetingPerfApi } from "../api";
import { AffiliationMenu } from "../components/affiliation-menu";
import { Avatar } from "../components/avatar";
import { SpeakerCard, type SpeakerSummary } from "../components/speaker-card";
import { CATEGORY_KEYS, CATEGORY_LABELS, type Speaker } from "../types";

// ── Pure helpers ────────────────────────────────────────────────────────────

/** Human-friendly relative date from an ISO timestamp string. */
function relativeDate(iso: string): string {
	const date = new Date(iso);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
	if (diffDays === 0) return "Today";
	if (diffDays === 1) return "Yesterday";
	if (diffDays < 7) return `${diffDays}d ago`;
	const diffWeeks = Math.floor(diffDays / 7);
	if (diffWeeks < 5) return `${diffWeeks}w ago`;
	return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Format total speaking time in seconds to a human-readable string. */
function formatSpeakingTime(sec: number): string {
	if (sec <= 0) return "—";
	if (sec >= 3600) {
		const h = Math.floor(sec / 3600);
		const m = Math.floor((sec % 3600) / 60);
		return `${h}h ${m}m`;
	}
	if (sec >= 60) {
		const m = Math.floor(sec / 60);
		const s = Math.floor(sec % 60);
		return `${m}m ${s}s`;
	}
	return `${Math.floor(sec)}s`;
}

/** Derive a SpeakerSummary from the enriched Speaker row + meId + onToggleTrack. */
function deriveSpeakerSummary(
	speaker: Speaker,
	meId: string | null,
	onToggleTrack: () => void,
	opts?: { onSetAffiliation?: (affiliation: "internal" | "external" | null) => void },
): SpeakerSummary {
	const { scoreSeries, latestScore, latestCategories, analysesCount } = speaker;

	const score = latestScore ?? 0;
	const delta =
		scoreSeries.length >= 2
			? (scoreSeries[scoreSeries.length - 1] ?? 0) - (scoreSeries[0] ?? 0)
			: 0;
	const sparkline = scoreSeries;
	const lastSeen = speaker.lastSeen ? relativeDate(speaker.lastSeen) : "—";

	// Derive top/weak dimension from latest categories
	let topDimension = { label: "—", score: 0 };
	let weakDimension = { label: "—", score: 0 };

	if (latestCategories) {
		let weakScore = 11; // sentinel — below any real 0–10 value
		for (const key of CATEGORY_KEYS) {
			const catScore = latestCategories[key] ?? 0;
			if (catScore > topDimension.score) {
				topDimension = { label: CATEGORY_LABELS[key], score: catScore };
			}
			if (catScore < weakScore) {
				weakScore = catScore;
				weakDimension = { label: CATEGORY_LABELS[key], score: catScore };
			}
		}
	}

	return {
		id: speaker.id,
		name: speaker.name,
		email: speaker.email,
		isMe: speaker.id === meId,
		tracked: speaker.tracked,
		score,
		delta,
		analyses: analysesCount,
		averageScore: speaker.averageScore,
		minScore: speaker.minScore,
		maxScore: speaker.maxScore,
		affiliation: speaker.affiliation,
		sparkline,
		topDimension,
		weakDimension,
		lastSeen,
		onToggleTrack,
		onSetAffiliation: opts?.onSetAffiliation,
	};
}

// ── Filter / sort types ──────────────────────────────────────────────────────

type FilterPill = "all" | "tracked" | "teammates" | "external";
type SortKey = "name" | "meetings" | "talkTime" | "avgScore" | "lastSeen";
type SortDir = "asc" | "desc";

const SORT_LABELS: Record<SortKey, string> = {
	name: "Name",
	meetings: "Meetings",
	talkTime: "Talk time",
	avgScore: "Avg score",
	lastSeen: "Last seen",
};
const SORT_KEYS: SortKey[] = ["avgScore", "name", "meetings", "talkTime", "lastSeen"];
/** First-click direction per column — text ascends, numbers/recency descend. */
const INITIAL_DIR: Record<SortKey, SortDir> = {
	name: "asc",
	meetings: "desc",
	talkTime: "desc",
	avgScore: "desc",
	lastSeen: "desc",
};

/**
 * One comparator for BOTH tiers, over raw Speaker fields. Missing values
 * (avgScore/lastSeen null) sort last regardless of direction — the
 * present-vs-missing branch runs before `dir` applies.
 */
function compareSpeakers(a: Speaker, b: Speaker, sort: SortKey, dir: SortDir): number {
	const isMissing = (s: Speaker): boolean =>
		sort === "avgScore" ? s.averageScore == null : sort === "lastSeen" ? s.lastSeen == null : false;
	const aMissing = isMissing(a);
	const bMissing = isMissing(b);
	if (aMissing !== bMissing) return aMissing ? 1 : -1;

	let cmp = 0;
	switch (sort) {
		case "name":
			cmp = a.name.localeCompare(b.name);
			break;
		case "meetings":
			cmp = a.meetingsCount - b.meetingsCount;
			break;
		case "talkTime":
			cmp = a.totalSpeakingSec - b.totalSpeakingSec;
			break;
		case "avgScore":
			cmp = (a.averageScore ?? 0) - (b.averageScore ?? 0);
			break;
		case "lastSeen":
			cmp = new Date(a.lastSeen ?? 0).getTime() - new Date(b.lastSeen ?? 0).getTime();
			break;
	}
	return dir === "asc" ? cmp : -cmp;
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function SpeakersListPage() {
	const [search, setSearch] = useState("");
	const [filter, setFilter] = useState<FilterPill>("all");
	const [sort, setSort] = useState<SortKey>("avgScore");
	const [dir, setDir] = useState<SortDir>("desc");

	const toggleSort = (key: SortKey) => {
		if (sort === key) {
			setDir((d) => (d === "asc" ? "desc" : "asc"));
		} else {
			setSort(key);
			setDir(INITIAL_DIR[key]);
		}
	};

	/** Clickable table-header label with the active-column direction indicator. */
	const sortLabel = (key: SortKey, text: string, align?: "right") => (
		<button
			type="button"
			onClick={() => toggleSort(key)}
			className={cn(
				"flex w-full items-center gap-0.5 transition-colors hover:text-foreground",
				align === "right" && "justify-end",
			)}
		>
			{text}
			{sort === key ? (
				dir === "asc" ? (
					<ChevronUp className="h-3 w-3 flex-none" />
				) : (
					<ChevronDown className="h-3 w-3 flex-none" />
				)
			) : null}
		</button>
	);

	const queryClient = useQueryClient();

	const speakersQuery = useQuery({
		queryKey: ["meeting-perf", "speakers"],
		// `affiliation` is an untyped text column on the wire row; the route's Zod
		// enum is the app-level constraint — narrow to the `Speaker` union here.
		queryFn: () => meetingPerfApi.listSpeakers() as Promise<Speaker[]>,
	});

	// Track / untrack mutation with optimistic update
	const trackMutation = useMutation({
		mutationFn: ({ id, tracked }: { id: string; tracked: boolean }) =>
			meetingPerfApi.setTracked({ params: { id }, input: { tracked } }),
		onMutate: async ({ id, tracked }) => {
			await queryClient.cancelQueries({ queryKey: ["meeting-perf", "speakers"] });
			const snapshot = queryClient.getQueryData<Speaker[]>(["meeting-perf", "speakers"]);
			queryClient.setQueryData<Speaker[]>(
				["meeting-perf", "speakers"],
				(old) => old?.map((s) => (s.id === id ? { ...s, tracked } : s)) ?? [],
			);
			return { snapshot };
		},
		onError: (_err, _vars, ctx) => {
			if (ctx?.snapshot) {
				queryClient.setQueryData(["meeting-perf", "speakers"], ctx.snapshot);
			}
			toast.error("Failed to update tracking");
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["meeting-perf", "speakers"] });
		},
	});

	// Set / clear (null) a speaker's internal-external affiliation, optimistically.
	const affiliationMutation = useMutation({
		mutationFn: ({
			id,
			affiliation,
		}: {
			id: string;
			affiliation: "internal" | "external" | null;
		}) => meetingPerfApi.setAffiliation({ params: { id }, input: { affiliation } }),
		onMutate: async ({ id, affiliation }) => {
			await queryClient.cancelQueries({ queryKey: ["meeting-perf", "speakers"] });
			const snapshot = queryClient.getQueryData<Speaker[]>(["meeting-perf", "speakers"]);
			queryClient.setQueryData<Speaker[]>(
				["meeting-perf", "speakers"],
				(old) => old?.map((s) => (s.id === id ? { ...s, affiliation } : s)) ?? [],
			);
			return { snapshot };
		},
		onError: (_err, _vars, ctx) => {
			if (ctx?.snapshot) {
				queryClient.setQueryData(["meeting-perf", "speakers"], ctx.snapshot);
			}
			toast.error("Failed to update affiliation");
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["meeting-perf", "speakers"] });
		},
	});

	const speakers = speakersQuery.data ?? [];
	// Derive meId from the speaker flagged isPrimary — no separate summary fetch needed.
	const meId = speakers.find((s) => s.isPrimary)?.id ?? null;

	// Tier A: tracked speakers (based on the tracked flag). Sort the RAW Speaker
	// rows (You pinned first, then the shared comparator) BEFORE deriving card
	// summaries, so both tiers order by the exact same fields.
	const tracked = speakers
		.filter((s) => s.tracked)
		.sort((a, b) => {
			const aMe = a.id === meId;
			const bMe = b.id === meId;
			if (aMe !== bMe) return aMe ? -1 : 1;
			return compareSpeakers(a, b, sort, dir);
		});
	const notTracked = speakers.filter((s) => !s.tracked);

	// One search+chip predicate over raw Speaker rows so both tiers agree.
	// Teammates/External: the persisted affiliation wins; the legacy has-email
	// heuristic applies only to UNSET speakers (back-compat for old rows).
	const searchLower = search.toLowerCase();
	const matchesFilter = (s: Speaker): boolean => {
		if (
			search &&
			!s.name.toLowerCase().includes(searchLower) &&
			!(s.email ?? "").toLowerCase().includes(searchLower)
		) {
			return false;
		}
		if (filter === "teammates") {
			return s.affiliation === "internal" || (s.affiliation == null && s.email != null);
		}
		if (filter === "external") {
			return s.affiliation === "external" || (s.affiliation == null && s.email == null);
		}
		return true;
	};

	const filteredTracked: SpeakerSummary[] = tracked
		.filter(matchesFilter) // filter === "tracked" passes everything in this tier
		.map((s) =>
			deriveSpeakerSummary(s, meId, () => trackMutation.mutate({ id: s.id, tracked: false }), {
				onSetAffiliation: (affiliation) => affiliationMutation.mutate({ id: s.id, affiliation }),
			}),
		);

	// Not-tracked: hidden entirely when the "Tracked" chip is active.
	const filteredNotTracked = notTracked
		.filter((s) => filter !== "tracked" && matchesFilter(s))
		.sort((a, b) => compareSpeakers(a, b, sort, dir));

	// Total unique speakers
	const uniqueTotal = speakers.length;

	const isLoading = speakersQuery.isLoading;
	const hasError = speakersQuery.error;

	return (
		<>
			<PageHeader
				icon={Users}
				title="Speakers"
				badges={uniqueTotal > 0 ? <Badge tone="subtle">{uniqueTotal}</Badge> : undefined}
				subtitle="Everyone tracked across your analyzed meetings."
				actions={
					<>
						<Link to="/meeting-perf/upload" search={{ source: "fireflies" }}>
							<Button variant="secondary" size="sm">
								<Download className="h-3.5 w-3.5" /> Import
							</Button>
						</Link>
						<Link to="/meeting-perf/upload">
							<Button size="sm">
								<Plus className="h-3.5 w-3.5" /> New meeting
							</Button>
						</Link>
					</>
				}
			/>

			<Toolbar
				left={
					<>
						<div className="relative">
							<Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-ink-tertiary" />
							<Input
								className="h-8 w-52 pl-8 text-body-sm"
								placeholder="Search speakers…"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
						</div>
						<Pill active={filter === "all"} onClick={() => setFilter("all")}>
							All
						</Pill>
						<Pill active={filter === "tracked"} onClick={() => setFilter("tracked")}>
							Tracked
						</Pill>
						{/* Teammates ≈ speakers with a known email; External ≈ without — approximate */}
						<Pill active={filter === "teammates"} onClick={() => setFilter("teammates")}>
							Teammates
						</Pill>
						<Pill active={filter === "external"} onClick={() => setFilter("external")}>
							External
						</Pill>
					</>
				}
				right={
					<>
						{/* Sort-key picker drives the card tier (no column headers there);
						    it shares state with the table headers below. */}
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="ghost" size="sm">
									<ArrowUpDown className="size-3.5" />
									{SORT_LABELS[sort]}
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								{SORT_KEYS.map((key) => (
									<DropdownMenuItem
										key={key}
										onClick={() => {
											setSort(key);
											setDir(INITIAL_DIR[key]);
										}}
									>
										{SORT_LABELS[key]}
									</DropdownMenuItem>
								))}
							</DropdownMenuContent>
						</DropdownMenu>
						<Button
							variant="ghost"
							size="sm"
							aria-label={dir === "asc" ? "Ascending" : "Descending"}
							onClick={() => setDir((d) => (d === "asc" ? "desc" : "asc"))}
						>
							{dir === "asc" ? (
								<ChevronUp className="size-3.5" />
							) : (
								<ChevronDown className="size-3.5" />
							)}
						</Button>
					</>
				}
			/>

			<PageBody className="overflow-y-auto px-6 py-5">
				{hasError ? (
					<p className="text-body-sm text-destructive">Could not load speakers.</p>
				) : null}

				{!isLoading && !hasError ? (
					<div className="space-y-6">
						{/* Section A — Tracked (rich cards) */}
						<section>
							<div className="mb-3 flex items-baseline justify-between">
								<div className="flex items-center gap-2">
									<h3 className="card-title text-ink">Tracked</h3>
									<Badge tone="subtle">{filteredTracked.length}</Badge>
								</div>
							</div>
							{filteredTracked.length === 0 ? (
								<div className="rounded-lg border border-hairline bg-surface-1">
									<EmptyState
										small
										icon={Users}
										title="No tracked speakers yet"
										body={
											search
												? "No tracked speakers match your search."
												: "Use + Track on a speaker below to pin them here."
										}
									/>
								</div>
							) : (
								<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
									{filteredTracked.map((s) => (
										<SpeakerCard key={s.id} speaker={s} />
									))}
								</div>
							)}
						</section>

						{/* Section B — Not tracked (dense rows) — omit if empty */}
						{filteredNotTracked.length > 0 ? (
							<section>
								<div className="mb-3 flex items-baseline justify-between">
									<div className="flex items-center gap-2">
										<h3 className="card-title text-ink">Not tracked</h3>
										<Badge tone="subtle">{filteredNotTracked.length}</Badge>
									</div>
								</div>
								<div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
									<ListHeader
										columns={[
											{ label: sortLabel("name", "Speaker"), width: "1.6fr" },
											{
												label: sortLabel("meetings", "Meetings", "right"),
												width: "90px",
												align: "right",
											},
											{
												label: sortLabel("talkTime", "Talk time", "right"),
												width: "90px",
												align: "right",
											},
											{
												label: sortLabel("avgScore", "Avg", "right"),
												width: "64px",
												align: "right",
											},
											{
												label: "Min / Max",
												width: "80px",
												align: "right",
											},
											{ label: sortLabel("lastSeen", "Last seen"), width: "110px" },
											{ label: "", width: "110px" },
											{ label: "", width: "116px" },
											{ label: "", width: "22px" },
										]}
									/>
									{filteredNotTracked.map((s) => (
										<ListRow
											key={s.id}
											cells={[
												{
													value: (
														<span className="flex items-center gap-2.5">
															<Avatar name={s.name} size="md" className="h-6 w-6 text-[9px]" />
															<span>
																<span className="font-medium text-ink">{s.name}</span>
																{s.email ? (
																	<span className="ml-2 text-meta text-ink-subtle">{s.email}</span>
																) : (
																	<span className="ml-2 text-meta text-ink-tertiary">No email</span>
																)}
															</span>
														</span>
													),
													width: "1.6fr",
												},
												{
													value: s.meetingsCount,
													muted: true,
													width: "90px",
													align: "right",
												},
												{
													value: formatSpeakingTime(s.totalSpeakingSec),
													muted: true,
													width: "90px",
													align: "right",
												},
												{
													value: s.averageScore ?? "—",
													muted: true,
													width: "64px",
													align: "right",
												},
												{
													value: `${s.minScore ?? "—"} / ${s.maxScore ?? "—"}`,
													muted: true,
													width: "80px",
													align: "right",
												},
												{
													value: s.lastSeen ? relativeDate(s.lastSeen) : "—",
													muted: true,
													width: "110px",
												},
												{
													value: (
														<AffiliationMenu
															value={s.affiliation}
															onSet={(affiliation) =>
																affiliationMutation.mutate({ id: s.id, affiliation })
															}
														/>
													),
													width: "110px",
												},
												{
													value: (
														<Button
															variant="secondary"
															size="sm"
															onClick={(e) => {
																// The row is a <Link>; prevent its default navigation AND
																// stop the click bubbling so "+ Track" only tracks.
																e.preventDefault();
																e.stopPropagation();
																trackMutation.mutate({ id: s.id, tracked: true });
															}}
														>
															+ Track
														</Button>
													),
													width: "116px",
												},
												{
													value: <ChevronRight className="size-3.5 text-ink-subtle" />,
													width: "22px",
													align: "right",
												},
											]}
											render={(p) => (
												<Link to="/meeting-perf/speakers/$id" params={{ id: s.id }} {...p} />
											)}
										/>
									))}
								</div>
							</section>
						) : null}
					</div>
				) : null}

				{isLoading ? <p className="text-body-sm text-ink-subtle">Loading…</p> : null}
			</PageBody>
		</>
	);
}
